import { useEffect, useMemo, useState } from 'react'

export const MediaQueryEnum = {
  xs: '(max-width: 575px)',
  sm: '(min-width: 576px) and (max-width: 767px)',
  md: '(min-width: 768px) and (max-width: 991px)',
  lg: '(min-width: 992px) and (max-width: 1199px)',
  xl: '(min-width: 1200px) and (max-width: 1599px)',
  xxl: '(min-width: 1600px)',
} as const

export type MediaQueryEnumType = keyof typeof MediaQueryEnum

export const useMediaQuery = (
  queryKeys: MediaQueryEnumType | MediaQueryEnumType[]
) => {
  const isSsr = typeof window === 'undefined'

  const queries = useMemo(() => {
    const keysArray = Array.isArray(queryKeys) ? queryKeys : [queryKeys]
    return keysArray.map((size) => MediaQueryEnum[size])
  }, [queryKeys])

  const [matches, setMatches] = useState(() =>
    isSsr ? false : queries.some((query) => window.matchMedia(query).matches)
  )

  useEffect(() => {
    if (isSsr) return

    const mediaQueryLists = queries.map((query) => window.matchMedia(query))
    const listener = () => setMatches(mediaQueryLists.some((query) => query.matches))

    mediaQueryLists.forEach((query) => query.addEventListener('change', listener))
    return () =>
      mediaQueryLists.forEach((query) => query.removeEventListener('change', listener))
  }, [isSsr, queries])

  return matches
}

//  helper to detect mobile screen (xs + sm)
export const useIsMobile = () => useMediaQuery(['xs', 'sm'])

export default useMediaQuery
